import { SamplePollLanguage } from '../poll/header.class';
import config from './sample-poll-seo.config.json';

/**
 * SEO-Strings und lokalisierte URL-Segmente fuer Sample-Poll-Meta-Tags.
 *
 * Single source of truth sind die dic.json-Files in whocan-app
 * (`general.{lang}.dic.json` -> `samplePoll.seo`, `routes.{lang}.dic.json` ->
 * `app.samplePollsList`/`app.samplePollMatrixSuffix`). Die hier importierte
 * `sample-poll-seo.config.json` wird daraus GENERIERT
 * (`scripts/generate-sample-poll-seo-config.mjs`, laeuft in
 * `npm run build:whocan-lib`) — nichts hartkodiert, kein manuelles Sync.
 * Der Drift-Guard-Spec in whocan-app schlaegt fehl, wenn nach einer
 * dic.json-Aenderung die Regeneration fehlt.
 */
export interface SamplePollSeoStrings {
    fallbackTitle: string;
    fallbackDescription: string;
    titleSuffix: string;
}

/** Meta-Strings der Listing-Page (Quelle: `samplePoll.list.title`/`.intro` der dic.json). */
export interface SamplePollListMeta {
    title: string;
    description: string;
}

interface SamplePollSeoConfigEntry extends SamplePollSeoStrings {
    listTitle: string;
    listDescription: string;
    listSegment: string;
    matrixSuffix: string;
}

function configEntry(lang: SamplePollLanguage): SamplePollSeoConfigEntry {
    return config[lang];
}

function toSeoStrings(lang: SamplePollLanguage): SamplePollSeoStrings {
    const entry = configEntry(lang);
    return {
        fallbackTitle: entry.fallbackTitle,
        fallbackDescription: entry.fallbackDescription,
        titleSuffix: entry.titleSuffix,
    };
}

export const SAMPLE_POLL_SEO_BY_LANG: Record<SamplePollLanguage, SamplePollSeoStrings> = {
    en: toSeoStrings('en'),
    de: toSeoStrings('de'),
    es: toSeoStrings('es'),
    fr: toSeoStrings('fr'),
    it: toSeoStrings('it'),
    pt: toSeoStrings('pt'),
};

function toListMeta(lang: SamplePollLanguage): SamplePollListMeta {
    const entry = configEntry(lang);
    return {
        title: entry.listTitle,
        description: entry.listDescription,
    };
}

/**
 * Title/Description der Sample-Poll-Listing-Page (H1 + Intro der Page selbst) —
 * genutzt vom samplePollList-Zweig des ogProxy und vom RouteContextService.
 */
export const SAMPLE_POLL_LIST_META_BY_LANG: Record<SamplePollLanguage, SamplePollListMeta> = {
    en: toListMeta('en'),
    de: toListMeta('de'),
    es: toListMeta('es'),
    fr: toListMeta('fr'),
    it: toListMeta('it'),
    pt: toListMeta('pt'),
};

/** Title-Budget vor dem Brand-Suffix " | Whocan" — Total ~65 Zeichen SERP-Limit. */
export const TITLE_MAX_BEFORE_BRAND = 55;
/** Description-Budget — Google rendert mit "…" wenn laenger. */
export const DESCRIPTION_MAX = 155;
export const DEFAULT_BRAND = 'Whocan';

/** Lokalisierte Listing-URL-Segmente (ohne Slash) der Sample-Poll-Listen. */
export const SAMPLE_POLL_LIST_SEGMENT_BY_LANG: Record<SamplePollLanguage, string> = {
    en: configEntry('en').listSegment,
    de: configEntry('de').listSegment,
    es: configEntry('es').listSegment,
    fr: configEntry('fr').listSegment,
    it: configEntry('it').listSegment,
    pt: configEntry('pt').listSegment,
};

/**
 * Lokalisierte Matrix-/Results-Suffixe unter dem Sample-Poll-Slug
 * (`/{lang}/{list}/{slug}/{suffix}`).
 */
export const SAMPLE_POLL_MATRIX_SUFFIX_BY_LANG: Record<SamplePollLanguage, string> = {
    en: configEntry('en').matrixSuffix,
    de: configEntry('de').matrixSuffix,
    es: configEntry('es').matrixSuffix,
    fr: configEntry('fr').matrixSuffix,
    it: configEntry('it').matrixSuffix,
    pt: configEntry('pt').matrixSuffix,
};
